import { getUserMedia, Space } from '@mux/spaces-web';
import { Button, Col, LiveStream, Spinner2 } from 'components';
import { Api, ws } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

const Video = styled.video`
  width: 448px;
  height: ${(props) => (!props.hasStream ? '0' : '296px')};
`;

/**
 * Shows preview media at first,
 * switches to LiveStream component upon going live
 */
const GoLive = ({ closeModal }) => {
  const [loading, setLoading] = useState(false);
  const [stream, setStream] = useState(null);
  const [data, setData] = useState(null);
  const [live, setLive] = useState(false);
  const publisherRef = useRef(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const mounted = useRef(true);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (live) return;

    // Start tracks
    getMedia();

    // Stop tracks
    return () => {
      publisherRef.current?.audioTracks.forEach((x) => x.stop());
      publisherRef.current?.videoTracks.forEach((x) => x.stop());
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      publisherRef.current = null;
      mounted.current = null;
    };
  }, []);

  /**
   * Use two different media streams,
   * localStream is for displaying own media to the host
   * stream is what we publish to mux using the sdk
   */
  async function getMedia() {
    const localStream = await navigator.mediaDevices
      .getUserMedia({
        audio: true,
        video: {
          frameRate: 30,
          height: 616,
          width: 936
        }
      })
      .then((stream) => stream)
      .catch((error) => console.error('getUserMedia error', error));

    if (!localStream) return;

    const videoEl = document.getElementById('add-live-preview');
    videoEl.srcObject = localStream;
    videoEl.play();
    setStream(localStream);
    streamRef.current = localStream;
  }

  async function startStreaming() {
    setLoading(true);

    const [res, stream] = await Promise.all([
      Api.post('/streams/create', { isWeb: true }),
      getUserMedia({
        audio: true,
        video: true
      })
    ]);
    if (!res) return setLoading(false);

    /**
     * Create a new space/broadcast on Mux, and get the JWT token
     * associated with the current process and connect to the room
     */
    const space = new Space(res.jwt);
    const joined = await space.join();
    await joined.publishTracks(stream);

    // Cleanup each media connection if the comp is unmounted unmount
    if (!mounted.current) {
      joined?.audioTracks.forEach((x) => x.stop());
      joined?.videoTracks.forEach((x) => x.stop());
      streamRef.current?.getTracks().forEach((track) => track.stop());

      return;
    }

    publisherRef.current = joined;
    setData(res);

    // Start broadcast command
    const res2 = await Api.post('/streams/start', { liveStreamId: res._id });
    if (!res2) return setLoading(false);

    dispatch({
      type: 'SET_LIVE_STREAM',
      payload: res
    });

    setLoading(false);
    setLive(true);

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: t('goLive.youAreLive') }
    });
  }

  function cleanup() {
    publisherRef.current?.audioTracks.forEach((x) => x.stop());
    publisherRef.current?.videoTracks.forEach((x) => x.stop());
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    publisherRef.current = null;
    mounted.current = null;
    setLive(false);
    dispatch({ type: 'RESET_LIVE_STREAM_STATE' });
    ws.send('leaveRoom', { roomId: data._id });
    Api.post('/streams/end', { roomId: data._id });
    closeModal();
  }

  return (
    <Col center>
      <Col ht="296px" noFlex>
        {!stream && <Spinner2 />}

        <Video ref={videoRef} id="add-live-preview" hasStream={stream} muted />
      </Col>

      <Col center>
        <Col noFlex>
          <Button
            marg="24px 0 0"
            pad="0 24px"
            loading={loading}
            disabled={!stream || live}
            onClick={startStreaming}
            text={t('add.startStreaming')}
          />
        </Col>
      </Col>

      {live && (
        <LiveStream
          isHost
          visible
          data={data}
          localStream={streamRef.current}
          closeModal={cleanup}
          modalBg="transparent"
        />
      )}
    </Col>
  );
};

export default GoLive;
